import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { ArrowLeft, Moon, Sun, Zap, History, Share2 } from 'lucide-react';
import './Auth.css';
import ForgotPassword from './ForgotPassword';
import { isTeamAPoint, isTeamBPoint } from '../utils/scoringOrder';

function Auth({ setToken, setUser, nightMode, toggleNightMode, onGuestMode }) {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [publicMatches, setPublicMatches] = useState([]);
  const [showAllPublicMatches, setShowAllPublicMatches] = useState(false);
  const [selectedPublicMatch, setSelectedPublicMatch] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    fetchPublicMatches();
  }, []);

  const fetchPublicMatches = async () => {
    try {
      const limit = showAllPublicMatches ? 50 : 5;
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/matches/public?limit=${limit}`);
      setPublicMatches(response.data);
    } catch (error) {
      console.error('Error fetching public matches:', error);
    }
  };

  useEffect(() => {
    fetchPublicMatches();
  }, [showAllPublicMatches]);

  const generateChartData = (match) => {
    const scoringOrder = match.scoringOrder || [];
    let scoreDataA = 0;
    let scoreDataB = 0;
    return scoringOrder.map((team, index) => {
      if (isTeamAPoint(team)) scoreDataA++;
      if (isTeamBPoint(team)) scoreDataB++;
      // Timeouts (TA1, TA2, TB1, TB2) don't increment scores
      const leadingTeam = scoreDataA > scoreDataB ? match.teamA : (scoreDataB > scoreDataA ? match.teamB : 'Tie');
      return {
        totalPoints: index + 1,
        [match.teamA]: scoreDataA,
        [match.teamB]: scoreDataB,
        pointDifferential: Math.abs(scoreDataA - scoreDataB),
        leadingTeam: leadingTeam,
        barColor: leadingTeam === match.teamA ? "#28a745" : (leadingTeam === match.teamB ? "#dc3545" : "#6c757d")
      };
    });
  };

  const renderScoreProgressionChart = (match) => {
    const chartData = generateChartData(match);

    if (!chartData.length) {
      return <p className="no-chart-data">No scoring data available for this match.</p>;
    }

    return (
      <div className="score-chart">
        <h4>Score Progression</h4>
        <ResponsiveContainer width="100%" height={450}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 15, left: 10, bottom: 30 }}>
            <XAxis 
              dataKey="totalPoints" 
              tick={{ fontSize: 10 }}
              label={{ value: 'Points', position: 'insideBottom', offset: -5, style: { fontSize: '10px' } }} 
            />
            <YAxis 
              yAxisId="left" 
              tick={{ fontSize: 10 }}
              label={{ value: 'Score', angle: -90, position: 'insideLeft', style: { fontSize: '10px' } }} 
            />
            <YAxis 
              yAxisId="right" 
              orientation="right" 
              tick={{ fontSize: 10 }}
              label={{ value: 'Diff', angle: 90, position: 'insideRight', style: { fontSize: '10px' } }} 
            />
            <Tooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="custom-tooltip">
                      <p style={{ margin: 0, padding: '2px 0', fontSize: '12px' }}>{`Point: ${label}`}</p>
                      <p style={{ margin: 0, padding: '2px 0', fontSize: '12px', color: '#28a745' }}>{`${selectedPublicMatch.teamA}: ${data[selectedPublicMatch.teamA]}`}</p>
                      <p style={{ margin: 0, padding: '2px 0', fontSize: '12px', color: '#dc3545' }}>{`${selectedPublicMatch.teamB}: ${data[selectedPublicMatch.teamB]}`}</p>
                      <p style={{ margin: 0, padding: '2px 0', fontSize: '12px' }}>{`Diff: ${data.pointDifferential}`}</p>
                      <p style={{ margin: 0, padding: '2px 0', fontSize: '12px', fontWeight: 'bold' }}>{`Leading: ${data.leadingTeam}`}</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend 
              wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
              iconSize={10}
            />
            <Bar 
              yAxisId="right" 
              dataKey="pointDifferential" 
              opacity={0.8} 
              name="Point Differential"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.barColor} />
              ))}
            </Bar>
            <Line yAxisId="left" type="monotone" dataKey={selectedPublicMatch.teamA} stroke="#28a745" strokeWidth={2} name={selectedPublicMatch.teamA} />
            <Line yAxisId="left" type="monotone" dataKey={selectedPublicMatch.teamB} stroke="#dc3545" strokeWidth={2} name={selectedPublicMatch.teamB} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleMatchClick = (match) => {
    // Always handle public match viewing locally in Auth component
    setSelectedPublicMatch(match);
  };

  const handleBackToMatches = () => {
    setSelectedPublicMatch(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const response = await axios.post(`${process.env.REACT_APP_API_URL}${endpoint}`, formData);
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        if (response.data.user) {
          try {
            localStorage.setItem('volleyball_user', JSON.stringify(response.data.user));
          } catch (storageError) {
            // Non-fatal.
          }
          if (setUser) setUser(response.data.user);
        }
        setToken(response.data.token);
      } else if (response.data.message) {
        setError(response.data.message);
      }
    } catch (error) {
      console.error('Auth error:', error.response?.data || error.message);
      setError(error.response?.data?.message || 'An error occurred');
    }
  };

  if (showForgotPassword) {
    return <ForgotPassword onBack={() => setShowForgotPassword(false)} nightMode={nightMode} />;
  }

  if (selectedPublicMatch) {
    return (
      <div className={`auth-container ${nightMode ? 'night-mode' : ''}`}>
        <div className="auth-panel">
          <div className="public-match-detail">
            <div className="match-detail-header">
              <button onClick={handleBackToMatches} className="back-btn">
                <ArrowLeft size={20} />
                Back to Public Matches
              </button>
              <h2>Public Match Details</h2>
            </div>
            
            <div className="match-info-card">
              <div className="match-teams">
                <span className={selectedPublicMatch.scoreA > selectedPublicMatch.scoreB ? 'winner' : ''}>
                  {selectedPublicMatch.teamA}
                </span>
                <span className="vs"> vs </span>
                <span className={selectedPublicMatch.scoreB > selectedPublicMatch.scoreA ? 'winner' : ''}>
                  {selectedPublicMatch.teamB}
                </span>
              </div>
              
              <div className="match-score">
                <span className={selectedPublicMatch.scoreA > selectedPublicMatch.scoreB ? 'winner-score' : ''}>
                  {selectedPublicMatch.scoreA}
                </span>
                <span> - </span>
                <span className={selectedPublicMatch.scoreB > selectedPublicMatch.scoreA ? 'winner-score' : ''}>
                  {selectedPublicMatch.scoreB}
                </span>
              </div>
              
              <div className="match-metadata">
                <div className="match-date">
                  <strong>Date:</strong> {new Date(selectedPublicMatch.date).toLocaleString()}
                </div>
                <div className="match-duration">
                  <strong>Duration:</strong> {formatTime(selectedPublicMatch.time)}
                </div>
                <div className="match-user">
                  <strong>Saved by:</strong> @{selectedPublicMatch.user?.username || 'Anonymous'}
                </div>
                {selectedPublicMatch.note && (
                  <div className="match-note">
                    <strong>Note:</strong> {selectedPublicMatch.note}
                  </div>
                )}
              </div>
            </div>
            
            {renderScoreProgressionChart(selectedPublicMatch)}
            
            <div className="night-mode-toggle">
              <button onClick={toggleNightMode} className="night-mode-btn">
                {nightMode ? <Sun size={16} /> : <Moon size={16} />}
                {nightMode ? 'Light mode' : 'Dark mode'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`auth-container ${nightMode ? 'night-mode' : ''}`}>
      <aside className="auth-hero">
        <div className="auth-hero-inner">
          <div className="vb-logo" aria-hidden="true">🏐</div>
          <h1>Score every rally like you’re on the sideline.</h1>
          <p>A live board built for rec leagues, school matches, and tournaments — tap to score, track serve and timeouts, then save the set.</p>
          <ul className="auth-features">
            <li><span><Zap size={14} /></span> Tap a team to add a point</li>
            <li><span><History size={14} /></span> Rally order, charts, and history</li>
            <li><span><Share2 size={14} /></span> Share public matches with anyone</li>
          </ul>
        </div>
      </aside>
      <div className="auth-panel">
        <div className="auth-brand-mobile">
          <div className="vb-logo" aria-hidden="true">🏐</div>
          <h1>Volleyball Score Tracker</h1>
        </div>
        <div className="auth-box">
          <h2>{isLogin ? 'Welcome back' : 'Create an account'}</h2>
          <p className="auth-subtitle">
            {isLogin ? 'Sign in to save matches and stats.' : 'Takes a minute. You can also continue as a guest.'}
          </p>
          {error && <p className="error-message">{error}</p>}
          <form onSubmit={handleSubmit}>
            {!isLogin && (
              <>
                <div className="auth-field">
                  <label htmlFor="name">Name</label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    autoComplete="name"
                  />
                </div>
                <div className="auth-field">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                  />
                </div>
              </>
            )}
            <div className="auth-field">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                name="username"
                placeholder="Username"
                value={formData.username}
                onChange={handleChange}
                required
                autoComplete="username"
              />
            </div>
            <div className="auth-field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete={isLogin ? 'current-password' : 'new-password'}
              />
            </div>
            <button type="submit" className="submit-btn">
              {isLogin ? 'Sign in' : 'Create account'}
            </button>
          </form>
          <div className="auth-options">
            <button type="button" onClick={() => setIsLogin(!isLogin)} className="toggle-auth-btn">
              {isLogin ? 'Need an account? Register' : 'Already have an account? Sign in'}
            </button>
            {isLogin && (
              <button type="button" onClick={() => setShowForgotPassword(true)} className="forgot-password-btn">
                Forgot password?
              </button>
            )}
            <button type="button" onClick={onGuestMode} className="guest-btn">
              Continue as guest
            </button>
          </div>
        </div>
        
        <div className="public-matches-section">
          <h3>Recent public matches</h3>
          {publicMatches.length > 0 ? (
            <div className="public-matches-container">
              <div className="public-match-cards">
                {publicMatches.map(match => (
                  <button
                    type="button"
                    key={match._id}
                    onClick={() => handleMatchClick(match)}
                    className="public-match-card"
                  >
                    <span className="pmc-teams">
                      <span className={match.scoreA > match.scoreB ? 'winner' : ''}>{match.teamA}</span>
                      {' vs '}
                      <span className={match.scoreB > match.scoreA ? 'winner' : ''}>{match.teamB}</span>
                    </span>
                    <span className="pmc-score">{match.scoreA}–{match.scoreB}</span>
                    <span className="pmc-meta">
                      {new Date(match.date).toLocaleDateString()} · @{match.user?.username || 'Anonymous'}
                    </span>
                  </button>
                ))}
              </div>
              <button 
                type="button"
                onClick={() => setShowAllPublicMatches(!showAllPublicMatches)} 
                className="view-more-btn"
              >
                {showAllPublicMatches ? 'Show less' : 'View more public matches'}
              </button>
            </div>
          ) : (
            <p className="no-matches">No public matches yet — be the first to share one.</p>
          )}
        </div>
        
        <div className="night-mode-toggle">
          <button type="button" onClick={toggleNightMode} className="night-mode-btn">
            {nightMode ? <Sun size={16} /> : <Moon size={16} />}
            {nightMode ? 'Light mode' : 'Dark mode'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Auth;